import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '../../contexts/LanguageContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * PublicSharedTaskPage — anonymous, read-only view of a task.
 *
 * Reached via `/share/task/<token>` and intentionally lives OUTSIDE the
 * authenticated routes so unauthenticated visitors can land on it. It fetches
 * a slimmed-down task payload from `/api/public/shared-task/<token>` and
 * presents it in a premium, brand-forward layout with an "Open in Edotask"
 * CTA and a "Powered by Edotask" footer.
 */
const PublicSharedTaskPage = () => {
  const { token } = useParams();
  const { t } = useLanguage();
  const [task, setTask] = useState(null);
  const [status, setStatus] = useState('loading'); // loading | ready | expired

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const resp = await axios.get(`${BACKEND_URL}/api/public/shared-task/${token}`);
        if (!cancelled) {
          setTask(resp.data);
          setStatus('ready');
        }
      } catch (_e) {
        if (!cancelled) setStatus('expired');
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const priorityAccent = (() => {
    const p = (task?.priority || '').toLowerCase();
    if (p === 'high') return 'border-t-red-500';
    if (p === 'medium') return 'border-t-amber-500';
    return 'border-t-emerald-500';
  })();

  const priorityChipClass = (() => {
    const p = (task?.priority || '').toLowerCase();
    if (p === 'high') return 'bg-red-50 text-red-700 ring-1 ring-red-200';
    if (p === 'medium') return 'bg-amber-50 text-amber-700 ring-1 ring-amber-200';
    return 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200';
  })();

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50/60 via-white to-indigo-50/40 text-slate-900 flex flex-col">
      {/* Header */}
      <header className="px-6 py-5 sm:py-6 flex items-center justify-between max-w-5xl w-full mx-auto">
        <div className="flex items-center gap-2">
          <span className="inline-block w-2.5 h-2.5 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600" />
          <span className="font-display text-lg sm:text-xl font-semibold tracking-tight text-blue-700">Edotask</span>
        </div>
        <span className="text-xs sm:text-sm text-slate-500 inline-flex items-center gap-1.5">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.7}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
          </svg>
          {t('sharedReadOnly') || 'Shared read-only'}
        </span>
      </header>

      {/* Body */}
      <main className="flex-1 px-4 sm:px-6 pb-32">
        <div className="max-w-3xl mx-auto">
          {status === 'loading' && (
            <div className="bg-white rounded-2xl border border-slate-200 p-8 animate-pulse">
              <div className="h-7 bg-slate-200 rounded w-2/3 mb-4" />
              <div className="h-4 bg-slate-100 rounded w-1/3 mb-8" />
              <div className="h-4 bg-slate-100 rounded w-full mb-2" />
              <div className="h-4 bg-slate-100 rounded w-5/6" />
            </div>
          )}

          {status === 'expired' && (
            <div className="bg-white rounded-2xl border border-slate-200 p-10 text-center">
              <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-amber-50 flex items-center justify-center">
                <svg className="w-7 h-7 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.6}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
                </svg>
              </div>
              <h1 className="text-lg font-semibold text-slate-900">{t('sharedTaskExpired') || 'This share link is no longer valid'}</h1>
              <p className="text-sm text-slate-500 mt-2">{t('sharedTaskExpiredDesc') || 'The owner has revoked it or the task was removed.'}</p>
            </div>
          )}

          {status === 'ready' && task && (
            <article className={`bg-white rounded-2xl border border-slate-200 border-t-4 ${priorityAccent} shadow-sm overflow-hidden`}>
              {/* Hero */}
              <div className="px-6 sm:px-8 pt-7 pb-6 border-b border-slate-100">
                <div className="flex flex-wrap items-center gap-2 mb-4">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${priorityChipClass}`}>
                    {task.priority || 'Medium'}
                  </span>
                  {task.category && (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700 ring-1 ring-blue-200">
                      {task.category}
                    </span>
                  )}
                  {task.completed && (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-600">
                      ✓ {task.completed ? 'Completed' : ''}
                    </span>
                  )}
                </div>
                <h1 className="font-display text-2xl sm:text-3xl font-semibold text-slate-900 tracking-tight leading-tight">
                  {task.title}
                </h1>
                {task.due_date && (
                  <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-slate-600">
                    <span className="inline-flex items-center gap-1.5">
                      <svg className="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.7}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      {new Date(task.due_date).toLocaleDateString()}
                      {task.time && <span className="text-slate-400 ml-1">· {task.time}</span>}
                    </span>
                    {task.location && (
                      <span className="inline-flex items-center gap-1.5">
                        <svg className="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.7}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M17.657 16.657L13.414 20.9a2 2 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                        {task.location}
                      </span>
                    )}
                  </div>
                )}
              </div>

              {/* Notes / description */}
              {(task.notes || task.description) && (
                <div className="px-6 sm:px-8 py-5 border-b border-slate-100">
                  <p className="text-xs font-medium uppercase tracking-wider text-slate-400 mb-2">Notes</p>
                  <p className="text-sm text-slate-700 whitespace-pre-line leading-relaxed">
                    {task.notes || task.description}
                  </p>
                </div>
              )}

              {/* Attachments — list with lock badge (no download for non-images on public view) */}
              {task.attachments && task.attachments.length > 0 && (
                <div className="px-6 sm:px-8 py-5 border-b border-slate-100">
                  <p className="text-xs font-medium uppercase tracking-wider text-slate-400 mb-3">
                    Attachments ({task.attachment_count || task.attachments.length})
                  </p>
                  <ul className="space-y-2">
                    {task.attachments.map((att, i) => (
                      <li key={i} className="flex items-center gap-3 px-3 py-2 rounded-lg bg-slate-50 border border-slate-100">
                        <span className="w-8 h-8 rounded-md bg-white border border-slate-200 flex items-center justify-center text-slate-500">
                          {String(att.content_type || '').startsWith('image/') ? '🖼' : '📎'}
                        </span>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-slate-700 truncate">{att.filename}</p>
                          <p className="text-xs text-slate-400">{att.content_type} · {att.size_mb} MB</p>
                        </div>
                        <span title="Login required to view" className="text-slate-400">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.7}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                          </svg>
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="px-6 sm:px-8 py-4 text-xs text-slate-400">
                {task.shared_at && (
                  <span>Shared on {new Date(task.shared_at).toLocaleString()}</span>
                )}
              </div>
            </article>
          )}
        </div>
      </main>

      {/* Sticky footer with branded CTA */}
      <footer className="fixed inset-x-0 bottom-0 backdrop-blur bg-white/80 border-t border-slate-200 z-40">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-3">
          <span className="text-xs sm:text-sm text-slate-500">
            {t('poweredByEdotask') || 'Powered by Edotask'} · <a href="https://edotask.com" className="text-blue-600 hover:underline">edotask.com</a>
          </span>
          <a
            href="https://app.edotask.com"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-gradient-to-r from-blue-600 to-indigo-600 text-white text-sm font-medium shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all"
            data-testid="public-share-open-in-edotask"
          >
            {t('openInEdotask') || 'Open in Edotask'}
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M14 5l7 7m0 0l-7 7m7-7H3" />
            </svg>
          </a>
        </div>
      </footer>
    </div>
  );
};

export default PublicSharedTaskPage;
